import { Colli } from '@frontend/colli/types';
import { EntityType } from '@frontend/common';
import { Logger } from '@frontend/logger';
import { Package, PackageType } from '@frontend/package/types';
import { PinCodeClient } from '@frontend/pin-code/api';
import { PinCode, PinCodeType } from '@frontend/pin-code/types';
import { TransactionClient, TransactionUserClient } from '@frontend/transaction/api';
import { Transaction, TransactionUser } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useScreen } from '../useScreen';

interface ViewProps {
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
    manualPin: string;
    setManualPin: Dispatch<SetStateAction<string>>;
    pin: PinCode | null;
    transaction: Transaction | null;
    receivers: TransactionUser[] | null;
    resolvePinCode: (code: string) => void;
    resolving: boolean;
    error: string | null;
    resetError: () => void;
}

const SENSOR_TIMEOUT = 10000;

const usePickUp = (): ViewProps => {
    const intl = useIntl();
    const { wizardId, store, onBack, updateWizardData, onSubmit: onContinue, navigationState } = useScreen();
    const [transaction, setTransaction] = useState<Transaction | null>(null);
    const [receivers, setReceivers] = useState<TransactionUser[] | null>(store?.data.receivers || null);
    const [pin, setPin] = useState<PinCode | null>(null);
    const [manualPin, setManualPin] = useState<string>('');
    const [mqttScanCallback, setMqttScanCallback] = useState<string | null>(null);
    const [resolving, setResolving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const resolvingRef = useRef(false);
    const pendingScanRef = useRef(false);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const codeNotFoundMessage = intl.formatMessage({
        id: 'HOME.ERRORS.CODE_NOT_FOUND',
        defaultMessage: 'No matching identification found. Please try again.'
    });

    const unlinkedIdentificationMessage = intl.formatMessage({
        id: 'kiosk.wizard.pick-up-scan-qr-code.unlinked-identification',
        description: 'Error shown when a scanned code is recognized but not linked to anything',
        defaultMessage: 'This code was recognized but is not linked to anything yet. Please try a different code or contact support.'
    });

    const clearSensorTimeout = useCallback(() => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    }, []);

    const finishResolving = useCallback(() => {
        clearSensorTimeout();
        pendingScanRef.current = false;
        resolvingRef.current = false;
        setResolving(false);
    }, [clearSensorTimeout]);

    const onFail = useCallback(
        (message: string = codeNotFoundMessage) => {
            finishResolving();
            setError(message);
        },
        [codeNotFoundMessage, finishResolving]
    );

    const fetchPinForTransaction = useCallback(async (identifiedTransaction: Transaction) => {
        const pinResult = await PinCodeClient.fetchPinCodes({
            entity_id: identifiedTransaction.id,
            entity_type: EntityType.TRANSACTION,
            type: PinCodeType.PICK_UP
        }).catch(() => ({ results: [], count: 0 }));

        if (pinResult.count > 0) {
            setPin(pinResult.results[0]);
        }
    }, []);

    const applyTransaction = useCallback(
        async (identifiedTransaction: Transaction) => {
            setTransaction(identifiedTransaction);
            await fetchPinForTransaction(identifiedTransaction);
            finishResolving();
        },
        [fetchPinForTransaction, finishResolving]
    );

    const fetchTransactionById = useCallback(
        async (accountId: string, transactionId: string) => {
            try {
                const identifiedTransaction = await TransactionClient.fetchTransaction(accountId, transactionId);
                await applyTransaction(identifiedTransaction);
            } catch (fetchError) {
                Logger.error('Error fetching identified transaction:', fetchError);
                onFail();
            }
        },
        [applyTransaction, onFail]
    );

    useScanners({
        id: 'PICK_UP',
        onDeviceSensorReceived: (code) => {
            setManualPin(code);
            setError(null);
            pendingScanRef.current = true;
            resolvingRef.current = true;
            setResolving(true);
            clearSensorTimeout();
            sensorTimeoutRef.current = setTimeout(() => {
                onFail();
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
            setMqttScanCallback(code);
        }
    });

    useIdentification({
        id: 'PICK_UP',
        onNoMatchingIdentificationFound: () => {
            if (!pendingScanRef.current) return;
            onFail();
        },
        onUnlinkedIdentificationFound: () => {
            if (!pendingScanRef.current) return;
            onFail(unlinkedIdentificationMessage);
        },
        onUserIdentified: () => {
            if (!pendingScanRef.current) return;
            onFail();
        },
        onPackageIdentified: (pkg: Package) => {
            if (!pendingScanRef.current) return;
            if (pkg.type !== PackageType.PICK_UP) {
                onFail();
                return;
            }
            const accountId = navigationState.iot?.account_id;
            if (!accountId || !pkg.transaction_id) {
                onFail();
                return;
            }
            void fetchTransactionById(accountId, pkg.transaction_id);
        },
        onColliIdentified: (colli: Colli) => {
            if (!pendingScanRef.current) return;
            const accountId = navigationState.iot?.account_id;
            if (!accountId || !colli.transaction_id) {
                onFail();
                return;
            }
            void fetchTransactionById(accountId, colli.transaction_id);
        },
        onTransactionIdentified: (identifiedTransaction: Transaction) => {
            if (!pendingScanRef.current) return;
            void applyTransaction(identifiedTransaction);
        }
    });

    const resolvePinCode = async (code: string, fromScan = false) => {
        if (resolvingRef.current && !fromScan) return;
        setError(null);
        resolvingRef.current = true;
        setResolving(true);
        PinCodeClient.fetchPinCodes({ code, entity_type: EntityType.TRANSACTION, type: PinCodeType.PICK_UP })
            .then((result) => {
                if (result.count === 1) {
                    finishResolving();
                    setPin(result.results[0]);
                } else if (!fromScan) {
                    onFail();
                }
            })
            .catch((lookupError) => {
                Logger.error('Error resolving scan:', lookupError);
                if (!fromScan) {
                    onFail();
                }
            })
            .finally(() => {
                if (!pendingScanRef.current) {
                    resolvingRef.current = false;
                    setResolving(false);
                }
            });
    };

    useEffect(() => {
        if (mqttScanCallback) {
            resolvePinCode(mqttScanCallback, true).finally(() => {
                setMqttScanCallback(null);
            });
        }
    }, [mqttScanCallback]);

    useEffect(() => {
        const fetchTransaction = async () => {
            if (pin) {
                const fetchedTransaction = await TransactionClient.fetchTransaction(pin.account_id, pin.entity_id);
                setTransaction(fetchedTransaction);
            }
        };
        fetchTransaction();
    }, [pin]);

    useEffect(() => {
        if (!receivers && transaction)
            TransactionUserClient.fetchTransactionUsers({ transaction_id: transaction.id }).then((res) =>
                setReceivers(res.results.filter((u) => u.type === 'receiver'))
            );
        else if (store?.data.receiver) setReceivers([store.data.receiver]);
    }, [receivers, transaction]);

    useEffect(() => {
        return () => {
            clearSensorTimeout();
        };
    }, [clearSensorTimeout]);

    const onSubmit = async () => {
        if (!wizardId || !store) return;
        if (!transaction) return;

        updateWizardData({
            transaction,
            pin_code: pin,
            receivers
        });

        const nextStep = store?.steps.find((step) => step.screen === ScreenType.PICK_UP && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    };

    return {
        submitDisabled: !transaction,
        onSubmit,
        onBack,
        manualPin,
        setManualPin,
        pin,
        transaction,
        receivers,
        resolvePinCode: (code: string) => resolvePinCode(code),
        resolving,
        error,
        resetError: () => setError(null)
    };
};

export default usePickUp;
